import { useCallback, useEffect, useState } from 'react';
import Cropper from 'react-easy-crop';
import { Dialog, DialogContent, IconButton, Slider } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import i18n from '../../utils/i18n';
import CustomButton from './CustomButton';

const OUTPUT_SIZE = 1024;
const MIN_ZOOM = 1;
const MAX_ZOOM = 3;
const ZOOM_STEP = 0.25;

const loadImage = (src) =>
	new Promise((resolve, reject) => {
		const image = new Image();
		image.onload = () => resolve(image);
		image.onerror = () => reject(new Error('Image could not be loaded'));
		image.src = src;
	});

// Renders the selected area into a square PNG. The round mask in the dialog
// is only a preview: the avatar is displayed as a circle everywhere else, so
// the stored picture stays a plain 1:1 image.
const renderSquareImage = async (src, cropArea) => {
	const image = await loadImage(src);
	const canvas = document.createElement('canvas');
	canvas.width = OUTPUT_SIZE;
	canvas.height = OUTPUT_SIZE;
	const ctx = canvas.getContext('2d');
	if (!ctx) throw new Error('Canvas is not supported');

	// Transparent PNGs would otherwise get a black background in some viewers
	ctx.fillStyle = '#ffffff';
	ctx.fillRect(0, 0, OUTPUT_SIZE, OUTPUT_SIZE);
	ctx.imageSmoothingQuality = 'high';
	ctx.drawImage(
		image,
		cropArea.x,
		cropArea.y,
		cropArea.width,
		cropArea.height,
		0,
		0,
		OUTPUT_SIZE,
		OUTPUT_SIZE
	);

	return canvas.toDataURL('image/png');
};

const ImageCropDialog = ({ open, imageSrc, onCancel, onConfirm }) => {
	const [crop, setCrop] = useState({ x: 0, y: 0 });
	const [zoom, setZoom] = useState(1);
	const [croppedArea, setCroppedArea] = useState(null);
	const [isProcessing, setIsProcessing] = useState(false);
	const [error, setError] = useState(null);

	// Start from a clean state every time a new image is picked
	useEffect(() => {
		setCrop({ x: 0, y: 0 });
		setZoom(1);
		setCroppedArea(null);
		setError(null);
	}, [imageSrc]);

	const handleCropComplete = useCallback((_, areaPixels) => {
		setCroppedArea(areaPixels);
	}, []);

	const changeZoom = (value) =>
		setZoom(Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, value)));

	const handleConfirm = async () => {
		if (!imageSrc || !croppedArea) return;
		setIsProcessing(true);
		setError(null);
		try {
			onConfirm(await renderSquareImage(imageSrc, croppedArea));
		} catch (err) {
			console.error('Error cropping image:', err);
			setError(i18n.t('provider.cropfailed'));
		} finally {
			setIsProcessing(false);
		}
	};

	return (
		<Dialog
			open={open}
			onClose={onCancel}
			PaperProps={{
				sx: {
					width: '420px',
					maxWidth: 'calc(100% - 32px)',
					margin: '16px',
					borderRadius: '18px',
					overflow: 'hidden',
				},
			}}>
			<DialogContent sx={{ padding: '18px' }}>
				<div
					style={{
						fontSize: '16px',
						fontWeight: 700,
						color: '#0b0f31',
						margin: '4px 0 12px',
					}}>
					{i18n.t('provider.cropimagetitle')}
				</div>
				<div
					style={{
						position: 'relative',
						width: '100%',
						height: 300,
						background: '#0b0f31',
						borderRadius: 8,
						overflow: 'hidden',
					}}>
					{imageSrc && (
						<Cropper
							image={imageSrc}
							crop={crop}
							zoom={zoom}
							aspect={1}
							cropShape='round'
							showGrid={false}
							onCropChange={setCrop}
							onZoomChange={setZoom}
							onCropComplete={handleCropComplete}
						/>
					)}
				</div>

				<div
					style={{
						display: 'flex',
						alignItems: 'center',
						gap: '8px',
						marginTop: '12px',
					}}>
					<IconButton
						size='small'
						aria-label={i18n.t('provider.cropzoomout')}
						onClick={() => changeZoom(zoom - ZOOM_STEP)}
						disabled={zoom <= MIN_ZOOM}
						sx={{ color: '#4b5080' }}>
						<RemoveIcon />
					</IconButton>
					<Slider
						value={zoom}
						min={MIN_ZOOM}
						max={MAX_ZOOM}
						step={0.05}
						aria-label={i18n.t('provider.cropzoom')}
						onChange={(_, value) => setZoom(value)}
						sx={{ color: '#1c32f3' }}
					/>
					<IconButton
						size='small'
						aria-label={i18n.t('provider.cropzoomin')}
						onClick={() => changeZoom(zoom + ZOOM_STEP)}
						disabled={zoom >= MAX_ZOOM}
						sx={{ color: '#4b5080' }}>
						<AddIcon />
					</IconButton>
				</div>

				{error && (
					<div style={{ color: '#d32f2f', fontSize: 14, marginTop: 8 }}>
						{error}
					</div>
				)}

				<div
					style={{
						display: 'flex',
						justifyContent: 'space-between',
						marginTop: '12px',
					}}>
					<CustomButton
						buttonName={i18n.t('providerdashboard.undo')}
						onClick={onCancel}
						isDisabled={isProcessing}
						buttonColor='#ffffff'
						hoverColor='#fafcfe'
						titleColor='#4b5080'
						titleHoverColor='#4b5080'
						height='44px'
						padding='12px 20px'
					/>
					<CustomButton
						buttonName={i18n.t('general.save')}
						onClick={handleConfirm}
						isDisabled={isProcessing || !croppedArea}
						buttonColor={isProcessing || !croppedArea ? '#cccccc' : '#1c32f3'}
						hoverColor={isProcessing || !croppedArea ? '#cccccc' : '#0036cc'}
						titleColor={isProcessing || !croppedArea ? '#666666' : '#ffffff'}
						titleHoverColor={
							isProcessing || !croppedArea ? '#666666' : '#e0e0e0'
						}
						height='44px'
						padding='12px 20px'
					/>
				</div>
			</DialogContent>
		</Dialog>
	);
};

export default ImageCropDialog;
